import React from 'react';
import styled from 'styled-components';

import { Label } from '@common/Label';
import { Command, State, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Stack } from '@mui/material';
import { Secrets } from '@common/Autocomplete/Secrets';
import { Button } from '@common/Button';
import { KeyOutlined, LoopOutlined } from '@mui/icons-material';
import { AICompletionNodeState, GatherUsingAINodeState } from '@components/Flow/Nodes';
import { Loader, LoadingContainer } from '@common/hooks/useLoader';
import { RequestError } from '@requests/common';
import { IntegrationSecret } from '@requests/secrets';
import { isPaidModel } from '@common/Autocomplete/Base';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

interface Props {
  command: Command;
  secrets: IntegrationSecret[];
  secretsLoading: boolean;
  secretsError: RequestError | null;
  refetch: () => void;
  setTab: React.Dispatch<string>;
}

export const getModelName = (model: string) => {
  try {
    if (model.includes('openai')) return 'OpenAI';
    if (model.includes('google')) return 'Gemini';
    if (model.includes('anthropic')) return 'Anthropic';
    if (model.includes('xai-org')) return 'Grok';
    if (model.includes('Groq')) return 'Groq';
    return 'Authenticated';
  } catch (error) {
    return 'Authenticated';
  }
};

export const getModelKey = (model: string) => {
  try {
    if (model.includes('openai')) return 'OPENAI_API_KEY' as const;
    if (model.includes('google')) return 'GOOGLE_API_KEY' as const;
    if (model.includes('anthropic')) return 'ANTHROPIC_API_KEY' as const;
    if (model.includes('xai-org')) return 'GROK_API_KEY' as const;
    if (model.includes('Groq')) return 'GROQ_API_KEY' as const;
    return null;
  } catch (error) {
    return null;
  }
};

export const ModelAuthKey: React.FC<Props> = ({ command, setTab, secrets, secretsLoading, refetch, secretsError }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { service_id } = useParams();

  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);
  const state = command.state as GatherUsingAINodeState | AICompletionNodeState;

  const noSecrets = secrets.length === 0;
  const model = state?.model || '';

  const key = getModelKey(model);
  const modelName = getModelName(model);

  const secretName = key ? authentication?.[key] : null;
  const authRequired = (!secretName || !secrets?.find((s) => s.identifier === secretName)) && isPaidModel(model);

  const changeModel = () => {
    if (!key) return;
    dispatch(tabActions.setNodeAuthProvider({ key, value: null }));
    dispatch(tabActions.nodeInputUpdate({ nodeId: command.node.id, fieldId: 'model', value: '' }));
    dispatch(tabActions.setSaved(false));
    setTab('1');
  };

  const isSecretDeleted =
    !secretsLoading && secretName && !secrets?.find((s) => s.identifier === secretName) && !secretsError;

  const setModelAuthKey = (value: string | null) => {
    if (!key) return;
    dispatch(tabActions.setNodeAuthProvider({ key, value }));
    setTab('1');
  };

  if (secretsLoading) {
    return (
      <LoadingContainer>
        <Loader />
      </LoadingContainer>
    );
  }

  if (secretsError) {
    return (
      <Alert
        severity='error'
        action={
          <Button customColor='red' onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        The client failed to load the secrets. If this issue persists, try refreshing your page.
      </Alert>
    );
  }

  if (noSecrets && !secretsError) {
    return (
      <>
        <Alert severity='warning' sx={{ whiteSpace: 'pre-wrap' }}>
          {modelName} models require a valid <strong>API KEY</strong> to generate completions.
          {`\n\nYou have not created any secrets yet. Click the button below to create a secret, and then come back to this tab.`}
        </Alert>
        {isSecretDeleted && (
          <Alert severity='error'>
            A previously used secret ({secretName}) has since been deleted. Please select an existing secret below.
          </Alert>
        )}
        <Stack direction='row' spacing={1} width='100%'>
          <Button fullWidth onClick={changeModel} startIcon={<LoopOutlined />}>
            Change Model
          </Button>
          <Button
            fullWidth
            customColor='primary'
            onClick={() => navigate(`/${service_id}/secrets`)}
            startIcon={<KeyOutlined />}
          >
            Create Secret
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <Container>
      {authRequired && (
        <Alert severity='warning'>
          <p>
            {modelName} models require a valid <strong>API KEY</strong> to generate completions. Add your key as a
            secret to select it below.
          </p>
        </Alert>
      )}

      <Stack spacing={1}>
        <Label
          title={`${modelName} Model Authentication`}
          tooltip={`${modelName} models require a valid API key. You can save your ${modelName} API key as a secret and use it here. This input determines the auth to use for the entire workflow, so if you have multiple nodes using ${modelName} models, you only have to set the auth once.`}
        />
        <Secrets
          refetch={refetch}
          value={secretName || ''}
          loading={secretsLoading}
          secrets={secrets.filter((secret) => secret.type === 'bearer')}
          onChange={(_, item) => {
            if (!item?.value) return;
            setModelAuthKey(item.value.identifier);
            dispatch(tabActions.setSaved(false));
          }}
          placeholder='Secret'
        />
        {isSecretDeleted && (
          <Alert severity='error'>
            A previously used secret ({secretName}) has since been deleted. Please select an existing secret below.
          </Alert>
        )}
      </Stack>
      {authRequired && (
        <Stack direction='row' spacing={1} width='100%'>
          <Button fullWidth onClick={changeModel} startIcon={<LoopOutlined />}>
            Change Model
          </Button>
          <Button fullWidth onClick={() => navigate(`/${service_id}/secrets`)} startIcon={<KeyOutlined />}>
            Create Secret
          </Button>
        </Stack>
      )}
    </Container>
  );
};
